/**
 * Workload-Steckbrief P2 (NC-1280, FE) — Auto-Fakten + Gap-Befunde.
 *
 * Two blocks, both read-only. The Steckbrief documents and warns; it never gates
 * (issue AC: "Befunde erscheinen in der Leseansicht, ohne die Bearbeitung zu
 * blockieren"), so nothing here disables editing or blocks a save.
 *
 * The load-bearing detail is `unevaluated`: rules the server could not check
 * because an input was unavailable. They are rendered as "konnte nicht geprüft
 * werden" — NOT folded into "keine Befunde". A tutor who reads "no findings" when
 * three rules were never evaluated has been told something false, and the API went
 * out of its way to keep the two apart.
 */

import React from 'react';
import { Alert, Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { ProfileAutoFacts, ProfileGapResult, ProfileGapRule } from '../../../services/types';
import { formatDateSafe } from '../../../utils/dateFormat';

interface Props {
  facts?: ProfileAutoFacts | null;
  gaps?: ProfileGapResult | null;
}

/** What each rule checks — shown when it could NOT be evaluated, so the gap is nameable. */
const RULE_SUBJECT: Record<ProfileGapRule, string> = {
  G1: 'Kostenstelle angelegt und aktiv',
  G2: 'Kostenstelle fachlich = technisch',
  G3: 'ownerCostCenter am Workload gesetzt',
  G4: '24/7-Betrieb unterstützt',
  G5: 'Ausgabeziel verfügbar',
  G7: 'Wiedervorlage fällig',
};

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', gap: 2, py: 0.5 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 220 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

export const ProfileFactsAndGaps: React.FC<Props> = ({ facts, gaps }) => {
  // Pre-NC-1280 response, or no profile yet: render nothing rather than empty
  // sections that look like a failed load.
  if (!facts && !gaps) return null;

  return (
    <Box sx={{ mt: 3 }} data-testid="profile-facts-and-gaps">
      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Nutzung (automatisch ermittelt)
      </Typography>

      {!facts || !facts.available ? (
        <Typography variant="body2" data-testid="facts-unavailable">
          Keine Daten — die Lauf-Historie ist derzeit nicht abrufbar.
        </Typography>
      ) : facts.runCount === 0 ? (
        // A workload that was never run is a legitimate state and a meaningful
        // statement in itself — not an error, and not "0 % Fehlerquote".
        <Typography variant="body2" data-testid="facts-no-runs">
          Noch kein Lauf mit diesem Workload.
          {facts.excludedTestRuns > 0 &&
            ` (${facts.excludedTestRuns} Testlauf/-läufe nicht gezählt.)`}
        </Typography>
      ) : (
        <Box data-testid="facts-values">
          <Fact label="Läufe" value={facts.runCount} />
          <Fact
            label="Letzter Lauf"
            value={facts.lastRunAtUtc ? formatDateSafe(facts.lastRunAtUtc, 'dd.MM.yyyy') : '—'}
          />
          <Fact label="Teilnehmerplätze insgesamt" value={facts.participantsTotal} />
          <Fact
            label="Fehlerquote Provisionierung"
            value={
              facts.provisioningErrorRate === null
                ? '—'
                : `${Math.round(facts.provisioningErrorRate * 100)} % (${facts.runsWithProvisioningError} von ${facts.runCount})`
            }
          />
          {facts.excludedTestRuns > 0 && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {facts.excludedTestRuns} Testlauf/-läufe sind in diesen Zahlen nicht enthalten.
            </Typography>
          )}
        </Box>
      )}

      <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
        Befunde
      </Typography>

      {!gaps ? (
        <Typography variant="body2" data-testid="gaps-unavailable">
          Keine Prüfung verfügbar.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {gaps.gaps.length === 0 && gaps.unevaluated.length === 0 && (
            <Typography variant="body2" data-testid="gaps-none">
              Keine Befunde — der Steckbrief passt zum Plattform-Zustand.
            </Typography>
          )}

          {gaps.gaps.map((gap) => (
            <Alert
              key={gap.rule}
              severity={gap.severity}
              data-testid={`gap-${gap.rule}`}
              // Warnings only: the Steckbrief documents, it does not gate.
              sx={{ '& .MuiAlert-message': { width: '100%' } }}
            >
              <Typography variant="body2" fontWeight="medium">
                {gap.title}
              </Typography>
              <Typography variant="caption" component="div">
                {gap.detail}
              </Typography>
              {gap.field && (
                <Typography variant="caption" color="text.secondary" component="div">
                  Feld: {gap.field}
                </Typography>
              )}
            </Alert>
          ))}

          {gaps.unevaluated.length > 0 && (
            // NOT "keine Befunde": these rules were never checked. Saying nothing
            // about them would read as an all-clear the server never gave.
            <Box data-testid="gaps-unevaluated">
              <Typography variant="body2" color="text.secondary">
                Nicht geprüft (Eingaben nicht verfügbar):
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                {gaps.unevaluated.map((rule) => (
                  <Chip
                    key={rule}
                    label={`${rule}: ${RULE_SUBJECT[rule]}`}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default ProfileFactsAndGaps;
